import type {
  CurriculumDetail,
  CurriculumLayer,
  CurriculumNodeInput,
} from "../services/curriculum.service";

export type LessonDraft = {
  id: number;
  title: string;
  lessonId: string;
  editing?: boolean;
  /**
   * Whether the picked lesson is unpublished.
   *
   * Only known for a lesson chosen from search this session — the curriculum
   * detail reports each slot's version state, which is not the lesson's own
   * publish flag, so this is undefined after a reload.
   */
  isDraft?: boolean;
};

export type NodeDraft = {
  id: number;
  name: string;
  open: boolean;
  editing?: boolean;
  children: NodeDraft[];
  lessons: LessonDraft[];
};

let nextTemporaryId = -1;

export const newTemporaryId = (): number => nextTemporaryId--;

/** Label for an add control, naming the layer it creates and the parent it fills. */
export const addChildLabel = (childLayerName: string, parentName: string): string => {
  const child = (childLayerName || "entry").toLowerCase();
  const parent = parentName.trim();
  return parent ? `Add ${child} to ${parent}` : `Add ${child}`;
};

const persistedId = (id: number | null): number | null =>
  id !== null && id > 0 ? id : null;

export const toNodeInput = (
  nodes: NodeDraft[],
  parentId: number | null,
): CurriculumNodeInput[] =>
  nodes.map((node, index) => ({
    id: persistedId(node.id),
    parent_id: persistedId(parentId),
    name: node.name.trim(),
    sort_order: index,
    children: toNodeInput(node.children, node.id),
    lessons: node.lessons.map((lesson) => ({
      id: persistedId(lesson.id),
      lesson_id: lesson.lessonId.trim() ? Number(lesson.lessonId) : null,
      title: lesson.title.trim(),
      status: lesson.lessonId.trim() ? "published" : "placeholder",
    })),
  }));

/**
 * Preserve existing lesson slots when adding hierarchy layers by placing them
 * below a generated "General" branch. Authors can rename that branch after
 * the layer change.
 */
export const migrateLessonsToAddedLayers = (
  nodes: NodeDraft[],
  oldLayerCount: number,
  nextLayers: CurriculumLayer[],
): NodeDraft[] => {
  const oldLessonParentDepth = oldLayerCount - 2;
  const addedLayerCount = nextLayers.length - oldLayerCount;
  if (addedLayerCount <= 0) return nodes;

  const migrate = (current: NodeDraft[], depth: number): NodeDraft[] =>
    current.map((node) => {
      const children = migrate(node.children, depth + 1);
      if (depth !== oldLessonParentDepth || node.lessons.length === 0) {
        return { ...node, children };
      }

      let branch: NodeDraft | null = null;
      for (let index = nextLayers.length - 2; index >= oldLayerCount - 1; index--) {
        branch = {
          id: newTemporaryId(),
          name: "General",
          open: true,
          editing: false,
          children: branch ? [branch] : [],
          lessons: branch ? [] : node.lessons,
        };
      }

      return {
        ...node,
        children: [...children, ...(branch ? [branch] : [])],
        lessons: [],
      };
    });

  return migrate(nodes, 0);
};

/**
 * Whether a slot should carry the Draft marker.
 *
 * The server is authoritative when it answers at all — including when it says
 * the lesson is published, which must clear a marker the client set earlier.
 * Silence is not an answer: an older backend omits the field, and the marker
 * then falls back to what this session already learned by picking the lesson.
 */
const draftMarker = (
  lessonIsPublished: boolean | null | undefined,
  lessonId: number | null | undefined,
  knownDraftLessonIds: Set<string>,
): boolean | undefined => {
  if (lessonIsPublished === false) return true;
  if (lessonIsPublished === true) return undefined;
  return lessonId != null && knownDraftLessonIds.has(String(lessonId))
    ? true
    : undefined;
};

/** Lesson ids this tree already knows to be drafts. */
const draftLessonIdsIn = (nodes: NodeDraft[]): Set<string> => {
  const ids = new Set<string>();
  const walk = (current: NodeDraft[]) => {
    for (const node of current) {
      for (const lesson of node.lessons) {
        if (lesson.isDraft && lesson.lessonId) ids.add(lesson.lessonId);
      }
      walk(node.children);
    }
  };
  walk(nodes);
  return ids;
};

/**
 * Build the editable tree from a curriculum detail.
 *
 * `previous` is the tree currently on screen, and only matters for the Draft
 * marker: a backend that predates `lesson_is_published` says nothing about it,
 * and without a fallback a save would drop a marker the author had just set by
 * picking a draft. An explicit server value always wins, so once the backend
 * ships, a lesson that has since been published clears the stale marker rather
 * than inheriting it.
 */
export const buildTree = (
  detail: CurriculumDetail,
  previous: NodeDraft[] = [],
): NodeDraft[] => {
  const knownDraftLessonIds = draftLessonIdsIn(previous);
  const nodes = detail.nodes.filter((node) => !node.is_lesson);
  const byParent = (parentId: number | null, depth: number): NodeDraft[] =>
    nodes
      .filter((node) => node.parent_id === parentId && node.depth === depth)
      .sort((a, b) => a.sort_order - b.sort_order || a.id - b.id)
      .map((node) => ({
        id: node.id,
        name: node.name,
        open: true,
        editing: !node.name.trim(),
        children: byParent(node.id, depth + 1),
        lessons: detail.nodes
          .filter((lesson) => lesson.is_lesson && lesson.parent_id === node.id)
          .sort((a, b) => a.sort_order - b.sort_order || a.id - b.id)
          .map((lesson) => ({
            id: lesson.id,
            title: lesson.lessons[0]?.title || lesson.name,
            lessonId:
              lesson.lessons[0]?.lesson_id === null ||
              lesson.lessons[0]?.lesson_id === undefined
                ? ""
                : String(lesson.lessons[0].lesson_id),
            editing: !(lesson.lessons[0]?.title || lesson.name).trim(),
            isDraft: draftMarker(
              lesson.lessons[0]?.lesson_is_published,
              lesson.lessons[0]?.lesson_id,
              knownDraftLessonIds,
            ),
          })),
      }));
  return byParent(null, 0);
};

export type SlotSummary = { label: string; total: number; filled: number };

export function getSlotSummary(layers: CurriculumLayer[], tree: NodeDraft[]): SlotSummary[] {
  const intermediateSlots = layers.slice(1, -1).map((layer) => ({
    label: layer.name,
    total: 0,
    filled: 0,
  }));
  const lessonSlots: SlotSummary = { label: "Lessons", total: 0, filled: 0 };
  const tallyLessons = (lessons: LessonDraft[]) => {
    lessonSlots.total += lessons.length;
    lessonSlots.filled += lessons.filter((lesson) => !lesson.editing && lesson.title.trim()).length;
  };
  const tallyNodes = (nodes: NodeDraft[], level: number) => {
    nodes.forEach((node) => {
      const slot = intermediateSlots[level];
      if (slot) {
        slot.total += 1;
        if (!node.editing && node.name.trim()) slot.filled += 1;
      }
      tallyLessons(node.lessons);
      tallyNodes(node.children, level + 1);
    });
  };

  tree.forEach((grade) => {
    tallyLessons(grade.lessons);
    tallyNodes(grade.children, 0);
  });

  return [
    {
      label: "Grades",
      total: tree.length,
      filled: tree.filter((node) => !node.editing && node.name.trim()).length,
    },
    ...intermediateSlots,
    lessonSlots,
  ];
}

/**
 * A layer nobody has started is not a finished layer. `0/0` satisfies
 * `filled === total`, so the emptiness has to be excluded explicitly.
 */
export const isSlotComplete = (slot: SlotSummary): boolean =>
  slot.total > 0 && slot.filled === slot.total;

/**
 * Every lesson ID the curriculum holds, across all its slots. A lesson fills
 * at most one slot (the API refuses a second), so a swap checks against this.
 */
export function lessonIdsInTree(tree: NodeDraft[]): Set<string> {
  const ids = new Set<string>();
  const walk = (nodes: NodeDraft[]) => {
    nodes.forEach((node) => {
      node.lessons.forEach((lesson) => {
        // As the save sends it (`Number`), so "0502" and "502" are one lesson.
        const id = lesson.lessonId.trim();
        if (id) ids.add(String(Number(id)));
      });
      walk(node.children);
    });
  };
  walk(tree);
  return ids;
}
